/**
 * Transform API paths to point to the connect-api server.
 *
 * Handles two cases:
 * 1. Relative paths like /api/assets/downloads/:id/file
 * 2. Full URLs with wrong domain (e.g., LLM-generated URLs that incorrectly
 *    use the portal domain instead of connect-api)
 *
 * This is needed because LLMs may "helpfully" convert relative URLs to full URLs
 * using the wrong domain when generating markdown responses.
 */
export function getApiUrl(path?: string): string | undefined {
  if (!path) return path;

  const apiBase = import.meta.env.VITE_PROSCI_CONNECT_API_URL || '';

  // Case 1: Relative path starting with /api/
  if (path.startsWith('/api/')) {
    return `${apiBase}${path}`;
  }

  // Case 2: Full URL containing /api/ path - extract and rewrite
  // This handles LLM-generated URLs like http://portal.prosci.com/api/assets/downloads/:id/file, which should actually go to the connect-api server
  if (path.includes('/api/')) {
    const apiPathMatch = path.match(/\/api\/.*/);
    if (apiPathMatch) {
      return `${apiBase}${apiPathMatch[0]}`;
    }
  }

  return path;
}


export function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export function isTrustedDomain(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.prosci.com') ||
    hostname === 'prosci.com'
  );
}

/**
 * Validate if a return URL is safe to redirect to
 * Allows relative paths and URLs from trusted domains
 */
const PATHWAYS_BASE_URL = import.meta.env.VITE_PATHWAYS_BASE_URL || '';

export function isValidReturnUrl(url: string): boolean {
  if (!url) return false;

  try {
    // Resolve relative paths too: browsers read "/\evil.com" as "//evil.com", and
    // "javascript://x.prosci.com/%0a..." has a trusted-looking hostname.
    const urlObj = new URL(url, window.location.origin);
    if (urlObj.protocol !== 'https:' && urlObj.protocol !== 'http:') return false;
    if (urlObj.origin === window.location.origin) return true;

    const hostname = urlObj.hostname;

    if (PATHWAYS_BASE_URL) {
      const configuredUrl = new URL(PATHWAYS_BASE_URL);
      if (hostname === configuredUrl.hostname) {
        return true;
      }
    }

    return isTrustedDomain(hostname);
  } catch (e) {
    return false;
  }
}

export function appendDownloadSource(url: string, source: string): string {
  if (!url || !source) return url;
  try {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set('source', source);
    return urlObj.toString();
  } catch {
    return url;
  }
}

export function isPathwaysUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    
    if (PATHWAYS_BASE_URL) {
      const configuredUrl = new URL(PATHWAYS_BASE_URL);
      if (hostname === configuredUrl.hostname) {
        return true;
      }
    }
    
    if (!isTrustedDomain(hostname)) return false;
    
    return hostname.includes('pathways');
  } catch {
    return false;
  }
}
